import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchQuestions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_questions?select=*&order=question_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function saveQuestion(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_questions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updateQuestion(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_questions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

const QUESTION_TYPES = ['RADIO', 'TEXTAREA', 'TEXT', 'GRADE'];

function QuestionForm({
  data,
  setData,
  onSubmit,
  onCancel,
  isNew,
  saving
}) {
  const showChoices =
    data.type === 'RADIO' ||
    data.type === 'GRADE';

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      <div style={styles.formRow}>
        <div style={styles.field}>
          <label style={styles.label}>Order</label>

          <input
            style={styles.input}
            type="number"
            value={data.question_order}
            onChange={e =>
              setData(prev => ({
                ...prev,
                question_order: e.target.value
              }))
            }
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type</label>

          <select
            style={styles.input}
            value={data.type}
            onChange={e =>
              setData(prev => ({
                ...prev,
                type: e.target.value
              }))
            }
            disabled={
              data.type === 'GRADE' &&
              !isNew
            }
          >
            {QUESTION_TYPES.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>
          Section title
        </label>

        <input
          style={styles.input}
          type="text"
          value={data.section_title}
          onChange={e =>
            setData(prev => ({
              ...prev,
              section_title: e.target.value
            }))
          }
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>
          Section description
        </label>

        <textarea
          style={styles.textarea}
          value={data.section_description}
          onChange={e =>
            setData(prev => ({
              ...prev,
              section_description:
                e.target.value
            }))
          }
          rows={2}
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>
          Question prompt
        </label>

        <textarea
          style={styles.textarea}
          value={data.prompt}
          onChange={e =>
            setData(prev => ({
              ...prev,
              prompt: e.target.value
            }))
          }
          rows={3}
        />
      </div>

      {showChoices && (
        <div style={styles.field}>
          <label style={styles.label}>
            Choices (one per line)
          </label>

          <textarea
            style={styles.textarea}
            value={data.choices}
            onChange={e =>
              setData(prev => ({
                ...prev,
                choices: e.target.value
              }))
            }
            rows={5}
          />
        </div>
      )}

      <div style={styles.checkRow}>
        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={data.required}
            onChange={e =>
              setData(prev => ({
                ...prev,
                required: e.target.checked
              }))
            }
            style={{ marginRight: '8px' }}
          />

          Required
        </label>

        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={data.show_justification}
            onChange={e =>
              setData(prev => ({
                ...prev,
                show_justification:
                  e.target.checked
              }))
            }
            style={{ marginRight: '8px' }}
          />

          Show justification box
        </label>
      </div>

      {data.show_justification && (
        <div style={styles.field}>
          <label style={styles.label}>
            Justification prompt
          </label>

          <input
            style={styles.input}
            type="text"
            value={data.justification_prompt}
            onChange={e =>
              setData(prev => ({
                ...prev,
                justification_prompt:
                  e.target.value
              }))
            }
          />

          <label
            style={{
              ...styles.checkLabel,
              marginTop: '8px'
            }}
          >
            <input
              type="checkbox"
              checked={
                data.justification_required
              }
              onChange={e =>
                setData(prev => ({
                  ...prev,
                  justification_required:
                    e.target.checked
                }))
              }
              style={{
                marginRight: '8px'
              }}
            />

            Justification required
          </label>
        </div>
      )}

      <div style={styles.btnRow}>
        <button
          style={styles.btn}
          disabled={saving}
        >
          {saving
            ? 'Saving...'
            : isNew
              ? 'Add Question'
              : 'Save Changes'}
        </button>

        <button
          type="button"
          style={styles.secondaryBtn}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function GeneralSelfEvalQuestions({ session }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const emptyQuestion = {
    question_order: 0,
    section_title: '',
    section_description: '',
    prompt: '',
    type: 'RADIO',
    required: true,
    choices: '',
    show_justification: false,
    justification_prompt:
      'Justification/Explanation/Evidence:',
    justification_required: false,
    active: true
  };

  const [newQuestion, setNewQuestion] =
    useState(emptyQuestion);

  useEffect(() => {
    fetchQuestions()
      .then(setQuestions)
      .finally(() =>
        setLoading(false)
      );
  }, []);

  function parseChoices(str) {
    return str
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  }

  async function handleAdd(e) {
    e.preventDefault();

    setError('');

    if (!newQuestion.prompt.trim()) {
      return setError(
        'Enter a question prompt.'
      );
    }

    if (
      (
        newQuestion.type === 'RADIO' ||
        newQuestion.type === 'GRADE'
      ) &&
      !newQuestion.choices.trim()
    ) {
      return setError(
        'Enter at least two choices.'
      );
    }

    setSaving(true);

    try {
      const result =
        await saveQuestion({
          question_order:
            Number(
              newQuestion.question_order
            ),
          section_title:
            newQuestion.section_title.trim(),
          section_description:
            newQuestion.section_description.trim(),
          prompt:
            newQuestion.prompt.trim(),
          type:
            newQuestion.type,
          required:
            newQuestion.required,
          choices:
            JSON.stringify(
              parseChoices(
                newQuestion.choices
              )
            ),
          show_justification:
            newQuestion.show_justification,
          justification_prompt:
            newQuestion
              .justification_prompt
              .trim(),
          justification_required:
            newQuestion
              .justification_required,
          active: true,
          updated_at:
            new Date().toISOString()
        });

      setQuestions(prev =>
        [
          ...prev,
          result[0]
        ].sort(
          (a, b) =>
            a.question_order -
            b.question_order
        )
      );

      setNewQuestion(
        emptyQuestion
      );

      setAdding(false);

      setSuccess(
        'Question added.'
      );
    } catch (err) {
      setError(
        'Failed to save question.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();

    setError('');

    if (!editing.prompt.trim()) {
      return setError(
        'Enter a question prompt.'
      );
    }

    setSaving(true);

    try {
      const result =
        await updateQuestion(
          editing.id,
          {
            question_order:
              Number(
                editing.question_order
              ),
            section_title:
              editing
                .section_title
                .trim(),
            section_description:
              editing
                .section_description
                .trim(),
            prompt:
              editing.prompt.trim(),
            type:
              editing.type,
            required:
              editing.required,
            choices:
              JSON.stringify(
                parseChoices(
                  editing.choices || ''
                )
              ),
            show_justification:
              editing
                .show_justification,
            justification_prompt:
              editing
                .justification_prompt
                .trim(),
            justification_required:
              editing
                .justification_required,
            updated_at:
              new Date()
                .toISOString()
          }
        );

      setQuestions(prev =>
        prev
          .map(q =>
            q.id === editing.id
              ? result[0]
              : q
          )
          .sort(
            (a, b) =>
              a.question_order -
              b.question_order
          )
      );

      setEditing(null);

      setSuccess(
        'Question updated.'
      );
    } catch (err) {
      setError(
        'Failed to update question.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(
    question
  ) {
    if (
      question.type === 'GRADE' &&
      question.active
    ) {
      return setError(
        'The grade question cannot be deactivated.'
      );
    }

    const result =
      await updateQuestion(
        question.id,
        {
          active:
            !question.active,
          updated_at:
            new Date()
              .toISOString()
        }
      );

    setQuestions(prev =>
      prev.map(q =>
        q.id === question.id
          ? result[0]
          : q
      )
    );
  }

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <h2 style={styles.subheading}>
        Self-Evaluation Questions
      </h2>

      <p style={styles.muted}>
        These questions appear on every
        course's self-evaluation form.
        The GRADE question is required
        and cannot be deactivated.
        Deactivated questions are hidden
        from students but historical
        responses are retained.
      </p>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      <div style={styles.list}>
        {questions.map(q => {
          const isEditing =
            editing?.id === q.id;

          const choices = (() => {
            try {
              return JSON.parse(
                q.choices || '[]'
              );
            } catch {
              return [];
            }
          })();

          return (
            <div
              key={q.id}
              style={{
                ...styles.item,
                opacity:
                  q.active ? 1 : 0.6
              }}
            >
              {isEditing ? (
                <QuestionForm
                  data={editing}
                  setData={setEditing}
                  onSubmit={
                    handleUpdate
                  }
                  onCancel={() =>
                    setEditing(null)
                  }
                  isNew={false}
                  saving={saving}
                />
              ) : (
                <div
                  style={
                    styles.itemInner
                  }
                >
                  <div
                    style={
                      styles.itemLeft
                    }
                  >
                    <div
                      style={
                        styles.itemHeader
                      }
                    >
                      <span
                        style={
                          styles.itemOrder
                        }
                      >
                        {
                          q.question_order
                        }
                      </span>

                      <span
                        style={
                          styles.itemTitle
                        }
                      >
                        {q.section_title ||
                          'Untitled'}
                      </span>

                      <span
                        style={
                          styles.typePill
                        }
                      >
                        {q.type}
                      </span>

                      {!q.active && (
                        <span
                          style={
                            styles.inactivePill
                          }
                        >
                          Inactive
                        </span>
                      )}
                    </div>

                    <div
                      style={
                        styles.itemPrompt
                      }
                    >
                      {q.prompt}
                    </div>
                  </div>

                  <div
                    style={
                      styles.itemActions
                    }
                  >
                    <button
                      style={
                        styles.editBtn
                      }
                      onClick={() => {
                        setEditing({
                          ...q,
                          choices:
                            choices.join(
                              '\n'
                            )
                        });

                        setAdding(false);
                        setError('');
                        setSuccess('');
                      }}
                    >
                      Edit
                    </button>

                    {q.type !==
                      'GRADE' && (
                      <button
                        style={
                          q.active
                            ? styles.dangerBtn
                            : styles.restoreBtn
                        }
                        onClick={() =>
                          handleToggleActive(
                            q
                          )
                        }
                      >
                        {q.active
                          ? 'Deactivate'
                          : 'Restore'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {adding ? (
        <div style={styles.addBox}>
          <h3
            style={
              styles.addHeading
            }
          >
            Add Question
          </h3>

          <QuestionForm
            data={newQuestion}
            setData={setNewQuestion}
            onSubmit={handleAdd}
            onCancel={() => {
              setAdding(false);

              setNewQuestion(
                emptyQuestion
              );
            }}
            isNew={true}
            saving={saving}
          />
        </div>
      ) : (
        <button
          style={styles.addBtn}
          onClick={() => {
            setAdding(true);
            setEditing(null);
            setError('');
            setSuccess('');
          }}
        >
          + Add Question
        </button>
      )}
    </div>
  );
}

const styles = {
  subheading: {
    margin: '0 0 8px',
    fontSize: '18px',
    color: '#1f2933'
  },

  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '0 0 20px',
    lineHeight: '1.5'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border:
      '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px'
  },

  item: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px 16px',
    background: 'white'
  },

  itemInner: {
    display: 'flex',
    justifyContent:
      'space-between',
    gap: '16px',
    alignItems: 'flex-start'
  },

  itemLeft: {
    flex: 1,
    minWidth: 0
  },

  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    flexWrap: 'wrap'
  },

  itemOrder: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0
  },

  itemTitle: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#1f2933'
  },

  typePill: {
    background: '#f0f4f7',
    color: '#66788a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  inactivePill: {
    background: '#faeaea',
    color: '#a12a2a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  itemPrompt: {
    fontSize: '13px',
    color: '#66788a',
    lineHeight: '1.4'
  },

  itemActions: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0
  },

  form: {
    width: '100%'
  },

  formRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '0',
    flexWrap: 'wrap'
  },

  field: {
    marginBottom: '14px',
    flex: 1,
    minWidth: '200px'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  input: {
    width: '100%',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '14px',
    boxSizing: 'border-box',
    background: 'white'
  },

  textarea: {
    width: '100%',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '14px',
    boxSizing: 'border-box',
    background: 'white',
    resize: 'vertical'
  },

  checkRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '14px',
    flexWrap: 'wrap'
  },

  checkLabel: {
    fontSize: '14px',
    color: '#1f2933',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },

  btnRow: {
    display: 'flex',
    gap: '10px',
    marginTop: '4px'
  },

  btn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  secondaryBtn: {
    background:
      'transparent',
    color: '#66788a',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  editBtn: {
    background:
      'transparent',
    color: '#215b7d',
    border:
      '1px solid #b0cfe0',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '13px',
    cursor: 'pointer'
  },

  dangerBtn: {
    background:
      'transparent',
    color: '#a12a2a',
    border:
      '1px solid #e2b8b8',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '13px',
    cursor: 'pointer'
  },

  restoreBtn: {
    background:
      'transparent',
    color: '#215b7d',
    border:
      '1px solid #b0cfe0',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '13px',
    cursor: 'pointer'
  },

  addBox: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '20px',
    background: '#f7f9fa',
    marginTop: '8px'
  },

  addHeading: {
    margin: '0 0 16px',
    fontSize: '16px',
    color: '#1f2933'
  },

  addBtn: {
    background:
      'transparent',
    color: '#215b7d',
    border:
      '2px dashed #b0cfe0',
    borderRadius: '10px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginTop: '4px'
  }
};